import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface MessageTemplate {
  id: string;
  company_id: string;
  title: string;
  body: string;
  channel: string;
  is_active: boolean;
  sort_order: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export function useMessageTemplates(channel?: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['message-templates', user?.company_id, channel],
    enabled: !!user?.company_id,
    queryFn: async () => {
      let q = supabase
        .from('message_templates')
        .select('*')
        .eq('company_id', user!.company_id!)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (channel && channel !== 'all') {
        q = q.or(`channel.eq.${channel},channel.eq.all`);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as MessageTemplate[];
    },
  });
}

export function useAllMessageTemplates() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['message-templates-all', user?.company_id],
    enabled: !!user?.company_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('message_templates')
        .select('*')
        .eq('company_id', user!.company_id!)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data || []) as MessageTemplate[];
    },
  });
}

export function useCreateMessageTemplate() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { title: string; body: string; channel: string }) => {
      const { error } = await supabase.from('message_templates').insert({
        company_id: user!.company_id!,
        created_by: user!.id,
        title: input.title,
        body: input.body,
        channel: input.channel,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['message-templates'] });
      qc.invalidateQueries({ queryKey: ['message-templates-all'] });
    },
  });
}

export function useUpdateMessageTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; title?: string; body?: string; channel?: string; is_active?: boolean }) => {
      const { id, ...updates } = input;
      const { error } = await supabase.from('message_templates').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['message-templates'] });
      qc.invalidateQueries({ queryKey: ['message-templates-all'] });
    },
  });
}

export function useDeleteMessageTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('message_templates').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['message-templates'] });
      qc.invalidateQueries({ queryKey: ['message-templates-all'] });
    },
  });
}
